/**
 * Meta Pixel & Conversion API (CAPI) Integration
 * -----------------------------------------------
 * - Frontend: fbq() calls through this utility
 * - Backend: CAPI calls via /api/v1/pixel/event endpoint
 * - Deduplication: same event_id used in both
 */

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}

/** Generate a UUID v4 for event deduplication */
export function generateEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Check if pixel is loaded */
function isPixelLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

/** Pixel config from Vite env */
const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '';
const TEST_EVENT_CODE = import.meta.env.VITE_META_TEST_EVENT_CODE || '';
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

/** Advanced Matching user data (stored after checkout form fill) */
export interface PixelUserData {
  em?: string;   // email (raw — hashed by fbq automatically for Pixel, hashed manually for CAPI)
  ph?: string;   // phone
  fn?: string;   // first name
  ln?: string;   // last name
  ct?: string;   // city
  zp?: string;   // zip/postal code
}

let _advancedMatchData: PixelUserData = {};

/**
 * Re-initialize pixel with advanced matching data
 * Call after user fills in checkout form
 */
export function setAdvancedMatching(data: PixelUserData): void {
  _advancedMatchData = { ...data };
  if (!isPixelLoaded() || !PIXEL_ID) return;

  // Normalize phone: strip spaces/dashes, ensure 90XXXXXXXXXX format
  const ph = data.ph
    ? data.ph.replace(/\D/g, '').replace(/^0/, '90').replace(/^(?!90)/, '90')
    : undefined;

  window.fbq('init', PIXEL_ID, {
    em: data.em?.toLowerCase().trim(),
    ph,
    fn: data.fn?.toLowerCase().trim(),
    ln: data.ln?.toLowerCase().trim(),
    ct: data.ct?.toLowerCase().trim(),
    zp: data.zp?.trim(),
    country: 'tr',
  });
}

/** Standard event parameters */
export interface PixelEventParams {
  content_ids?: string[];
  content_name?: string;
  content_type?: string;
  value?: number;
  currency?: string;
  num_items?: number;
  search_string?: string;
  order_id?: string;
  quantity?: number;
  [key: string]: unknown;
}

/**
 * Fire a pixel event (frontend fbq + CAPI via backend)
 */
export function trackEvent(
  eventName: string,
  params: PixelEventParams = {},
  sendToCAPI = true,
): string {
  const eventId = generateEventId();
  const eventTime = Math.floor(Date.now() / 1000);

  // 1. Frontend fbq() call
  if (isPixelLoaded()) {
    const eventData: Record<string, unknown> = { eventID: eventId };
    if (TEST_EVENT_CODE) {
      eventData.test_event_code = TEST_EVENT_CODE;
    }
    window.fbq('track', eventName, { ...params }, eventData);
  }

  // 2. Backend CAPI call (fire-and-forget)
  if (sendToCAPI && API_BASE_URL) {
    _sendCAPIEvent({
      event_name: eventName,
      event_id: eventId,
      event_time: eventTime,
      event_source_url: window.location.href,
      custom_data: params,
      user_data: _advancedMatchData,
    });
  }

  return eventId;
}

/** Internal CAPI payload shape */
interface CAPIPayload {
  event_name: string;
  event_id: string;
  event_time: number;
  event_source_url: string;
  custom_data: PixelEventParams;
  user_data: PixelUserData;
}

/**
 * Send event data to backend CAPI relay endpoint (fire-and-forget)
 */
function _sendCAPIEvent(payload: CAPIPayload): void {
  try {
    const body = JSON.stringify({
      ...payload,
      fbp: _getCookie('_fbp'),
      fbc: _getCookie('_fbc'),
      user_agent: navigator.userAgent,
    });

    const endpoint = `${API_BASE_URL}/pixel/event`;

    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
    } else {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => { /* silently fail */ });
    }
  } catch {
    // Never break UX due to tracking errors
  }
}

/** Read a browser cookie value by name */
function _getCookie(name: string): string {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : '';
}

// ─── Convenience event helpers ───────────────────────────────────────────────

/** PageView — call on every route change */
export function trackPageView(): string {
  const eventId = generateEventId();
  const eventTime = Math.floor(Date.now() / 1000);
  if (isPixelLoaded()) {
    const eventData: Record<string, unknown> = { eventID: eventId };
    if (TEST_EVENT_CODE) eventData.test_event_code = TEST_EVENT_CODE;
    window.fbq('track', 'PageView', {}, eventData);
  }
  if (API_BASE_URL) {
    _sendCAPIEvent({
      event_name: 'PageView',
      event_id: eventId,
      event_time: eventTime,
      event_source_url: window.location.href,
      custom_data: {},
      user_data: _advancedMatchData,
    });
  }
  return eventId;
}

/** ViewContent — product detail page */
export function trackViewContent(params: {
  content_ids: string[];
  content_name: string;
  value: number;
  currency?: string;
}): string {
  return trackEvent('ViewContent', {
    content_ids: params.content_ids,
    content_name: params.content_name,
    content_type: 'product',
    value: Math.round(params.value),
    currency: params.currency ?? 'TRY',
  });
}

/** Search — when user performs a search */
export function trackSearch(searchString: string): string {
  return trackEvent('Search', { search_string: searchString });
}

/** AddToCart */
export function trackAddToCart(params: {
  content_ids: string[];
  content_name: string;
  value: number;
  quantity: number;
  currency?: string;
}): string {
  return trackEvent('AddToCart', {
    content_ids: params.content_ids,
    content_name: params.content_name,
    value: Math.round(params.value),
    currency: params.currency ?? 'TRY',
    quantity: params.quantity,
  });
}

/** AddToWishlist */
export function trackAddToWishlist(params: {
  content_ids: string[];
  value: number;
  currency?: string;
}): string {
  return trackEvent('AddToWishlist', {
    content_ids: params.content_ids,
    value: Math.round(params.value),
    currency: params.currency ?? 'TRY',
  });
}

/** InitiateCheckout — when entering checkout page */
export function trackInitiateCheckout(params: {
  value: number;
  num_items: number;
  content_ids: string[];
  currency?: string;
}): string {
  return trackEvent('InitiateCheckout', {
    value: Math.round(params.value),
    currency: params.currency ?? 'TRY',
    num_items: params.num_items,
    content_ids: params.content_ids,
  });
}

/** AddPaymentInfo — when payment method is selected */
export function trackAddPaymentInfo(params: {
  value: number;
  currency?: string;
}): string {
  return trackEvent('AddPaymentInfo', {
    value: Math.round(params.value),
    currency: params.currency ?? 'TRY',
  });
}

/**
 * Purchase — ONLY fire on successful order completion (thank you page)
 * NEVER call this at checkout start
 */
export function trackPurchase(params: {
  order_id: string;
  value: number;
  content_ids: string[];
  currency?: string;
}): string {
  return trackEvent('Purchase', {
    content_ids: params.content_ids,
    value: Math.round(params.value),
    currency: params.currency ?? 'TRY',
    content_type: 'product',
    order_id: params.order_id,
  });
}

/** Lead — contact/quote form submission */
export function trackLead(value?: number): string {
  const params: PixelEventParams = { currency: 'TRY' };
  if (value !== undefined) params.value = Math.round(value);
  return trackEvent('Lead', params);
}
